import React from 'react'

export interface SkeletonProps {
    variant?: 'card' | 'text' | 'profile' | 'list' | 'custom'
    width?: number | string
    height?: number | string
    radius?: number | string
    style?: React.CSSProperties
    children?: React.ReactNode
}

const keyframesId = 'numeo-skeleton-shimmer'
const keyframesStyle = `@keyframes ${keyframesId}{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}`

const Block = ({
    width,
    height,
    radius = 2,
    style,
}: {
    width?: number | string
    height: number | string
    radius?: number | string
    style?: React.CSSProperties
}) => {
    return (
        <div className="relative shrink-0 overflow-hidden bg-surface-tertiary" style={{ width: width ?? '100%', height, borderRadius: radius, ...style }}>
            <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
                <div className="absolute inset-0 animate-[numeo-skeleton-shimmer_1.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-surface-secondary to-transparent" />
            </div>
        </div>
    )
}

const CardSkeleton = () => {
    return (
        <div className="flex w-[130px] flex-col gap-2 rounded-lg p-2">
            <Block height={66} radius={8} />
            <div className="flex flex-col gap-1.5">
                <Block height={6} width="62%" radius={8} />
                <Block height={6} width="70%" radius={8} />
                <Block height={6} width="52%" radius={8} />
            </div>
        </div>
    )
}

const TextSkeleton = () => {
    return (
        <div className="flex w-[149px] flex-col gap-1.5">
            <Block height={6} />
            <Block height={6} width="81%" />
            <Block height={6} width="60%" />
            <Block height={6} />
            <Block height={6} width="44%" />
        </div>
    )
}

const ProfileSkeleton = () => {
    return (
        <div className="flex w-[97px] items-center gap-2">
            <Block width={24} height={24} radius={12} />
            <div className="flex flex-1 flex-col gap-1">
                <Block height={6} radius={8} />
                <Block height={6} width="72%" radius={8} />
            </div>
        </div>
    )
}

const ListSkeleton = () => {
    return (
        <div className="flex w-[239px] flex-col gap-2">
            {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-2">
                    <Block width={24} height={24} radius={8} />
                    <div className="flex flex-1 flex-col gap-1">
                        <Block height={6} />
                        <Block height={6} style={{ marginRight: 39 }} />
                    </div>
                </div>
            ))}
        </div>
    )
}

export const Skeleton = ({ variant = 'custom', width, height, radius, style, children }: SkeletonProps) => {
    return (
        <>
            <style>{keyframesStyle}</style>
            {variant === 'card' && <CardSkeleton />}
            {variant === 'text' && <TextSkeleton />}
            {variant === 'profile' && <ProfileSkeleton />}
            {variant === 'list' && <ListSkeleton />}
            {variant === 'custom' && <Block width={width} height={height ?? 20} radius={radius ?? 4} style={style} />}
            {children}
        </>
    )
}
